/**
 * Select — the shared dropdown primitive for every data-management screen
 * (PMA-UI wave, replacing the native `<select>` the client's screenshot
 * flagged as an unstyled grey OS overlay). Built on `@radix-ui/react-select`
 * so portal rendering, keyboard nav, ARIA and touch behaviour come for free;
 * this file only adds ProMatchAnalytics's visual language and two things
 * Radix doesn't ship: a filter box for long lists and a genuine bottom-sheet
 * presentation on phones (see the `.pma-select-*` rules in `index.css`).
 *
 * Compound API (shadcn-style), so callers compose exactly the shape they
 * need — flat lists, `SelectGroup`/`SelectLabel` optgroups (e.g. player
 * position by tactical group), or a `SelectSearchInput` for long lists
 * (teams, the 31 competition stages). For a searchable list, filtering is
 * NOT caller-managed ad hoc — use `useSelectFilter` (`useSelectFilter.ts`),
 * the one sanctioned filter for this whole primitive family (case-
 * insensitive, trimmed, accent-insensitive — see `selectFilter.ts`; also
 * used internally by `Combobox`). Hand-rolling a different comparison at a
 * call site is exactly the inconsistency this hook exists to prevent:
 *
 *   const { query, setQuery, visible } = useSelectFilter(teamOptions);
 *
 *   <Select value={code} onValueChange={setCode}>
 *     <SelectTrigger data-testid="team-select">
 *       <SelectValue placeholder="Select team" />
 *     </SelectTrigger>
 *     <SelectContent>
 *       <SelectSearchInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." />
 *       {visible.length === 0 && <SelectEmpty>No matches</SelectEmpty>}
 *       {visible.map((o) => (
 *         <SelectItem key={o.value} value={o.value} deprecated={o.deprecated}>
 *           {o.label}
 *         </SelectItem>
 *       ))}
 *     </SelectContent>
 *   </Select>
 *
 * Desktop: a floating popover (Radix Portal, escapes any overflow/scroll
 * container — the exact clipping failure a hand-rolled dropdown risks).
 * Phone (<640px): the same Content is restyled into a full-width bottom
 * sheet via CSS only (no JS viewport check, no separate mobile component) —
 * see `index.css`'s `:has([data-radix-popper-content-wrapper])` rules.
 */

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "../../lib/utils";
import { Check, ChevronDown, Search } from "../icons";

/**
 * Tracks the Root's open state so `SelectContent` can gate the phone-only
 * backdrop on it (see `SelectContent` below for why this exists — Radix's
 * `Select.Portal` always portals its children, even while closed, so a
 * plain sibling div has no built-in way to know open/closed).
 */
const SelectOpenContext = React.createContext(false);

type SelectProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>;

const Select = ({ open, defaultOpen, onOpenChange, ...props }: SelectProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  );
  const isControlled = open !== undefined;
  const currentOpen = isControlled ? open : uncontrolledOpen;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  return (
    <SelectOpenContext.Provider value={currentOpen}>
      <SelectPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </SelectOpenContext.Provider>
  );
};
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "input group flex cursor-pointer items-center justify-between gap-2 text-left",
      "data-[placeholder]:text-gray-400",
      "data-[disabled]:cursor-not-allowed data-[disabled]:bg-gray-50 data-[disabled]:text-gray-400",
      className,
    )}
    {...props}
  >
    <span className="truncate">{children}</span>
    <SelectPrimitive.Icon asChild>
      <ChevronDown
        size={13}
        className="shrink-0 text-gray-400 transition-transform duration-150 group-data-[state=open]:rotate-180"
      />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = "SelectTrigger";

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(
  (
    { className, children, position = "popper", sideOffset = 6, ...props },
    ref,
  ) => {
    // Radix's Select.Portal (unlike Popover.Portal) always portals its
    // children to document.body, even while closed, so `Select.Content` can
    // keep items registered for value-display/typeahead — Content itself then
    // internally chooses whether to render live or into an off-screen
    // fragment. A backdrop div is just a plain sibling with no such gating,
    // so it must not render at all while closed, or it leaks into the live
    // DOM as a permanent full-viewport click-blocker on phone widths (see
    // PMA-UI final verification defect 1). Reading the Root's open state via
    // context — not CSS — is what makes the closed case not render the node
    // at all instead of merely hiding it.
    const isOpen = React.useContext(SelectOpenContext);
    return (
      <SelectPrimitive.Portal>
        {/* Radix's Select.Portal forwards its children with `asChild` onto the
        underlying portal node, which requires exactly one element — the
        backdrop and Content are grouped under one wrapper div so Portal
        still only ever sees a single child. */}
        <div>
          {isOpen && <div className="pma-select-backdrop" aria-hidden="true" />}
          <SelectPrimitive.Content
            ref={ref}
            position={position}
            sideOffset={sideOffset}
            collisionPadding={8}
            data-pma-select-content=""
            className={cn(
              "pma-select-content z-50 overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-900 shadow-lg",
              position === "popper" &&
                "min-w-[var(--radix-select-trigger-width)] max-h-[min(24rem,var(--radix-select-content-available-height))]",
              className,
            )}
            {...props}
          >
            <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-1 text-gray-400">
              <ChevronDown size={13} className="rotate-180" />
            </SelectPrimitive.ScrollUpButton>
            <SelectPrimitive.Viewport className="p-1.5">
              {children}
            </SelectPrimitive.Viewport>
            <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-1 text-gray-400">
              <ChevronDown size={13} />
            </SelectPrimitive.ScrollDownButton>
          </SelectPrimitive.Content>
        </div>
      </SelectPrimitive.Portal>
    );
  },
);
SelectContent.displayName = "SelectContent";

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "font-condensed px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400",
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = "SelectLabel";

interface SelectItemOwnProps {
  /** Value the record already stores that is inactive/unrecognized — never
   *  offered for a new pick, only ever rendered because it's the current
   *  selection. Renders muted + italic, matching `admin.referenceData.deprecatedOption`. */
  deprecated?: boolean;
}

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> &
    SelectItemOwnProps
>(({ className, children, deprecated, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-pointer select-none items-center gap-2 rounded-lg py-2 pl-3 pr-8 text-sm text-gray-700 outline-none",
      // Signature touch: a left-rail accent bar on the highlighted/selected
      // row — the same inset-box-shadow technique index.css already uses to
      // mark a live match row in .data-rows, so the selector reads as part
      // of this app's system rather than an off-the-shelf component.
      "data-[highlighted]:bg-primary-50 data-[highlighted]:text-primary-900 data-[highlighted]:shadow-[inset_3px_0_0_theme(colors.primary.500)]",
      "data-[state=checked]:font-semibold data-[state=checked]:text-primary-700",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-40",
      deprecated && "italic text-gray-400 data-[state=checked]:text-gray-500",
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator className="absolute right-2.5 inline-flex text-primary-600">
      <Check size={13} />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
));
SelectItem.displayName = "SelectItem";

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("my-1 h-px bg-gray-100", className)}
    {...props}
  />
));
SelectSeparator.displayName = "SelectSeparator";

/**
 * Filter box for a long option list (teams, the 31 competition stages).
 * Purely a controlled input — it owns no matching logic itself, pair it
 * with `useSelectFilter` (`useSelectFilter.ts`) for `query`/`setQuery`, the
 * one sanctioned filter for this primitive family. Not a Radix primitive —
 * deliberately kept out of Radix's roving-focus/typeahead handling
 * (`stopPropagation` below) so typing filters instead of jumping to a
 * matching item by first letter. ArrowDown/Escape/Enter are allowed through
 * so the keyboard bridges cleanly into the (now-filtered) item list below
 * it.
 */
const SelectSearchInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, onKeyDown, ...props }, ref) => (
  <div className="sticky top-0 z-10 -mx-1.5 -mt-1.5 mb-1 flex items-center gap-2 border-b border-gray-100 bg-white/95 px-3 py-2 backdrop-blur-sm">
    <Search size={13} className="shrink-0 text-gray-400" />
    <input
      ref={ref}
      type="text"
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      className={cn(
        "w-full border-0 bg-transparent p-0 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:outline-none focus:ring-0",
        className,
      )}
      onKeyDown={(event) => {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          const content = event.currentTarget.closest(
            "[data-pma-select-content]",
          );
          const first = content?.querySelector<HTMLElement>(
            '[role="option"]:not([data-disabled])',
          );
          first?.focus();
        } else if (event.key !== "Escape" && event.key !== "Enter") {
          // Keep typing local to the box; let Radix's dismiss layer still
          // see Escape, and don't fight a real Enter-to-select.
          event.stopPropagation();
        }
        onKeyDown?.(event);
      }}
      {...props}
    />
  </div>
));
SelectSearchInput.displayName = "SelectSearchInput";

/** Sensible empty state for a category with zero offerable values — a
 *  non-interactive row, not a silently blank popup. */
function SelectEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="presentation"
      className="select-none px-3 py-6 text-center text-sm text-gray-400"
    >
      {children}
    </div>
  );
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectSearchInput,
  SelectEmpty,
};
